/**
 * Ranking Evaluation structure: admin tool for comparing and scoring
 * comment-ranking algorithms across conversation sets.
 *
 * Four tabs: Conversation Set, Side-by-Side comparison, Comment Scoring, and
 * Custom Rankers. Side-by-Side and Comment Scoring expect the instance key to
 * refer to a deployment, silo, and space. Conversation Set and Custom Rankers
 * use the singleton instance to refer to the dashboard itself. Access is gated
 * by the `RANKING_EVAL_CAPABILITY` capability. Tab implementations live in the
 * nested `structure/ranking-eval/` directory.
 */
import { ChartColumn, Compare, ListBoxes, Settings } from '@carbon/icons-react';
import { RANKING_EVAL_CAPABILITY } from '@repo/shared/roles-capabilities';
import { createThemedStyles } from '@repo/theme/styles';
import type { CommentConfig } from 'component/comment';
import { ActionEdit, ActionReply } from 'component/comment';
import { spacings } from 'component/constants';
import type { DashboardConfig, DashboardTab } from 'component/dashboard';
import type { SpacesTableConfig } from 'component/spaces-table';
import { useImportableEvalConversations, useSelectedEvalConversationSet } from 'data/ranking-eval';
import { ScrollView } from 'react-native';
import { ActionUpvote } from 'structure/question/format/audience-input';
import type { Structure } from 'util/stdtypes';
import { t } from 'util/stdtypes';

import { CommentScoringContent } from './ranking-eval/comment-scoring';
import { CustomRankersTab } from './ranking-eval/custom-rankers';
import { SideBySideContent } from './ranking-eval/side-by-side';
import {
    ActionsColumn,
    CommentsColumn,
    CreatedColumn,
    EmptySpacesWidget,
    NameColumn,
    SourceFilter,
    SpacesTab,
} from './ranking-eval/spaces-tab';

// Re-export the imported-space wire type for the tool's components.
export type { ExternalEvalSpace } from 'data/ranking-eval';

type RankingEvalConfig = DashboardConfig & SpacesTableConfig & Partial<CommentConfig>;

const RANKING_EVAL_DASHBOARD_TABS: DashboardTab[] = [
    { key: 'conversation-set', label: t('ranking-eval.tab.conversation_set', 'Conversation Set'), icon: ListBoxes },
    { key: 'side-by-side', label: t('ranking-eval.tab.side_by_side', 'Side by Side'), icon: Compare },
    { key: 'comment-scoring', label: t('ranking-eval.tab.comment_scoring', 'Comment Scoring'), icon: ChartColumn },
    { key: 'custom-rankers', label: t('ranking-eval.tab.custom_rankers', 'Custom Rankers'), icon: Settings },
];

export function useFirstConversationKey(): string | undefined {
    const selectedSet = useSelectedEvalConversationSet();
    const selectedKeys = selectedSet?.conversationKeys || [];
    const allConversations = useImportableEvalConversations();
    return selectedKeys.length > 0 ? selectedKeys[0] : Object.keys(allConversations)[0];
}

export function SpacesScreen() {
    const s = useRankingEvalScreenStyles();
    return (
        <ScrollView style={s.content}>
            <SpacesTab />
        </ScrollView>
    );
}

function CustomRankersScreen() {
    const s = useRankingEvalScreenStyles();
    return (
        <ScrollView style={s.content}>
            <CustomRankersTab />
        </ScrollView>
    );
}

function SideBySideScreen() {
    const s = useRankingEvalScreenStyles();
    return (
        <ScrollView style={s.content}>
            <SideBySideContent />
        </ScrollView>
    );
}

function CommentScoringScreen() {
    const s = useRankingEvalScreenStyles();
    return (
        <ScrollView style={s.content}>
            <CommentScoringContent />
        </ScrollView>
    );
}

const useRankingEvalScreenStyles = createThemedStyles((tokens) => ({
    content: {
        backgroundColor: tokens.colors.colorSurfacePrimary,
        padding: spacings.xl3,
    },
}));

export const RankingEvalStructure: Structure<RankingEvalConfig> = {
    key: 'ranking-eval',
    name: 'Ranking Evaluation',
    isAdminUi: true,
    subscreens: {
        'conversation-set': SpacesScreen,
        'comment-scoring/[instanceKey]': CommentScoringScreen,
        'side-by-side/[instanceKey]': SideBySideScreen,
        'custom-rankers': CustomRankersScreen,
    },
    receivesConfigDefaults: {
        // Dashboard configuration
        dashboardTabs: RANKING_EVAL_DASHBOARD_TABS,
        dashboardTitleLabel: t('ranking-eval.title', 'Ranking Evaluation'),
        dashboardCapability: RANKING_EVAL_CAPABILITY,

        // SpacesTable configuration
        columns: [NameColumn, CommentsColumn, CreatedColumn, ActionsColumn],
        tableTitleLabel: t('ranking-eval.spaces.title', 'Spaces'),
        spaceTableRightWidgets: [],
        filters: [SourceFilter],
        emptyTableWidget: EmptySpacesWidget,

        // Comment widgets - make conversations feel real
        commentActions: [ActionReply, ActionUpvote, ActionEdit],
        commentRightActions: [],
    },
};
